import { CurrencyAmount, JSBI, Token, Trade, Pair, ETHER, WETH, ChainId } from '@uniswap/sdk'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ArrowDown } from 'react-feather'
import ReactGA from 'react-ga'
import { Text } from 'rebass'
import styled, { ThemeContext } from 'styled-components'
import AddressInputPanel from '../../components/AddressInputPanel'
import { ButtonError, ButtonPrimary, ButtonConfirmed } from '../../components/Button'
import Card, { GreyCard } from '../../components/Card'
import Column, { AutoColumn } from '../../components/Column'
import ConfirmSwapModal from '../../components/swap/ConfirmSwapModal'
import CurrencyInputPanel from '../../components/CurrencyInputPanel'
import { AutoRow, RowBetween } from '../../components/Row'
import AdvancedSwapDetailsDropdown from '../../components/swap/AdvancedSwapDetailsDropdown'
import BetterTradeLink, { DefaultVersionLink } from '../../components/swap/BetterTradeLink'
import confirmPriceImpactWithoutFee from '../../components/swap/confirmPriceImpactWithoutFee'
import { ArrowWrapper, BottomGrouping, SwapCallbackError, Wrapper } from '../../components/swap/styleds'
import TradePrice from '../../components/swap/TradePrice'
import TokenWarningModal from '../../components/TokenWarningModal'
import ProgressSteps from '../../components/ProgressSteps'
import SwapHeader from '../../components/swap/SwapHeader'
import TradingView from '../../components/TradingView'

import { getTradeVersion } from '../../data/V1'
import { useActiveWeb3React } from '../../hooks'
import { useCurrency, useAllTokens } from '../../hooks/Tokens'
import { ApprovalState, useApproveCallbackFromTrade } from '../../hooks/useApproveCallback'
import useENSAddress from '../../hooks/useENSAddress'
import { useSwapCallback } from '../../hooks/useSwapCallback'
import useToggledVersion, { DEFAULT_VERSION, Version } from '../../hooks/useToggledVersion'
import useWrapCallback, { WrapType } from '../../hooks/useWrapCallback'
// import { useWalletModalToggle } from '../../state/application/hooks'
import { Field } from '../../state/swap/actions'
import {
  useDefaultsFromURLSearch,
  useDerivedSwapInfo,
  useSwapActionHandlers,
  useSwapState
} from '../../state/swap/hooks'
import { useExpertModeManager, useUserSlippageTolerance, useUserSingleHopOnly } from '../../state/user/hooks'
import { LinkStyledButton, TYPE } from '../../theme'
import { maxAmountSpend } from '../../utils/maxAmountSpend'
import { computeTradePriceBreakdown, warningSeverity } from '../../utils/prices'
import AppBody from '../AppBody'
import Loader from '../../components/Loader'
import { useIsTransactionUnsupported } from 'hooks/Trades'
// import UnsupportedCurrencyFooter from 'components/swap/UnsupportedCurrencyFooter'
import { isTradeBetter } from 'utils/trades'
import { RouteComponentProps } from 'react-router-dom'

import Web3Status from '../../components/Web3Status'
// import Sidebar from '../../components/Sidebar'

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  overflow-y: auto;

  @media (max-width: 768px) {
    min-height: 90vh;
    flex-direction: column;
  }
`

const ContentContainer = styled.div<{ align?: string; isGrid?: boolean }>`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: ${({ align }) => align || 'center'};
  padding: 2rem;
  padding-top: ${({ isGrid }) => (isGrid ? '10rem' : '0')};

  @media (max-width: 1440px) {
    padding-top: 5rem;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 0;
  }
`

const SwapIcon = styled.img`
  width: 16px;
  height: 16px;

  @media (max-width: 768px) {
    width: 20px;
    height: 20px;
  }
`

const SwapIconWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 27px;
  height: 27px;
  border-radius: 50%;
  background-color: #c1ff72;
  padding: 10px;
  position: absolute;
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    transition: all 0.3s ease;

    &:active {
      transform: translateX(-50%) scale(0.95);
    }
  }
`

const TradingViewSection = styled.div`
  width: 100%;
  max-width: 960px;
  margin-left: 256px;
  min-width: 680px;

  @media (max-width: 1440px) {
    margin-left: 220px;
  }

  @media (max-width: 768px) {
    margin-top: 1rem;
    padding: 0;
    margin: 0;
    min-width: 100%;
  }
`

const Toast = styled.div<{ type: 'success' | 'error' | 'info' }>`
  width: auto;
  background-color: ${({ type }) => (type === 'success' ? '#008000' : type === 'error' ? '#FF0000' : '#0000FF')};
  color: #fff;
  padding: 10px 20px;
  border-radius: 5px;
  text-align: center;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  border-radius: 5px;
  position: fixed;
  top: 10px;
  right: 20px;
  z-index: 1000;
  animation: toastAnimation 0.3s ease-in-out;

  @keyframes toastAnimation {
    from {
      transform: translateY(-100%);
    }
  }
  @media (max-width: 768px) {
    width: auto;
    max-width: 100%;
    margin: 0;
    top: 1rem;
    right: 10px;
  }
`

export default function Swap({ history }: RouteComponentProps) {
  const loadedUrlParams = useDefaultsFromURLSearch()
  const [shouldInvertPrices, setShouldInvertPrices] = useState<boolean>(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info' | ''; text: string }>({
    type: '',
    text: ''
  })
  // token warning stuff
  const [loadedInputCurrency, loadedOutputCurrency] = [
    useCurrency(loadedUrlParams?.inputCurrencyId),
    useCurrency(loadedUrlParams?.outputCurrencyId)
  ]
  const [dismissTokenWarning, setDismissTokenWarning] = useState<boolean>(false)
  const [isShowChart, setIsShowChart] = useState<boolean>(false)
  const urlLoadedTokens: Token[] = useMemo(
    () => [loadedInputCurrency, loadedOutputCurrency]?.filter((c): c is Token => c instanceof Token) ?? [],
    [loadedInputCurrency, loadedOutputCurrency]
  )
  const handleConfirmTokenWarning = useCallback(() => {
    setDismissTokenWarning(true)
  }, [])

  // dismiss warning if all imported tokens are in active lists
  const defaultTokens = useAllTokens()
  const importTokensNotInDefault =
    urlLoadedTokens &&
    urlLoadedTokens.filter((token: Token) => {
      return !Boolean(token.address in defaultTokens)
    })

  const { account } = useActiveWeb3React()
  const theme = useContext(ThemeContext)

  // toggle wallet when disconnected
  // const toggleWalletModal = useWalletModalToggle()

  // for expert mode
  const [isExpertMode] = useExpertModeManager()

  // get custom setting values for user
  const [allowedSlippage] = useUserSlippageTolerance()

  // swap state
  const { independentField, typedValue, recipient } = useSwapState()
  const {
    v1Trade,
    v2Trade,
    currencyBalances,
    parsedAmount,
    currencies,
    inputError: swapInputError
  } = useDerivedSwapInfo()

  const { wrapType, execute: onWrap, inputError: wrapInputError } = useWrapCallback(
    currencies[Field.INPUT],
    currencies[Field.OUTPUT],
    typedValue
  )
  const showWrap: boolean = wrapType !== WrapType.NOT_APPLICABLE
  const { address: recipientAddress } = useENSAddress(recipient)
  const toggledVersion = useToggledVersion()
  const tradesByVersion = {
    [Version.v1]: v1Trade,
    [Version.v2]: v2Trade
  }
  const trade = showWrap ? undefined : tradesByVersion[toggledVersion]
  const defaultTrade = showWrap ? undefined : tradesByVersion[DEFAULT_VERSION]

  const betterTradeLinkV2: Version | undefined =
    toggledVersion === Version.v1 && isTradeBetter(v1Trade, v2Trade) ? Version.v2 : undefined

  const parsedAmounts = showWrap
    ? {
        [Field.INPUT]: parsedAmount,
        [Field.OUTPUT]: parsedAmount
      }
    : {
        [Field.INPUT]: independentField === Field.INPUT ? parsedAmount : trade?.inputAmount,
        [Field.OUTPUT]: independentField === Field.OUTPUT ? parsedAmount : trade?.outputAmount
      }

  const { onSwitchTokens, onCurrencySelection, onUserInput, onChangeRecipient } = useSwapActionHandlers()
  const isValid = !swapInputError
  const dependentField: Field = independentField === Field.INPUT ? Field.OUTPUT : Field.INPUT

  const handleTypeInput = useCallback(
    (value: string) => {
      onUserInput(Field.INPUT, value)
    },
    [onUserInput]
  )
  const handleTypeOutput = useCallback(
    (value: string) => {
      onUserInput(Field.OUTPUT, value)
    },
    [onUserInput]
  )

  // reset if they close warning without tokens in params
  const handleDismissTokenWarning = useCallback(() => {
    setDismissTokenWarning(true)
    history.push('/swap/')
  }, [history])

  // modal and loading
  const [{ showConfirm, tradeToConfirm, swapErrorMessage, attemptingTxn, txHash }, setSwapState] = useState<{
    showConfirm: boolean
    tradeToConfirm: Trade | undefined
    attemptingTxn: boolean
    swapErrorMessage: string | undefined
    txHash: string | undefined
  }>({
    showConfirm: false,
    tradeToConfirm: undefined,
    attemptingTxn: false,
    swapErrorMessage: undefined,
    txHash: undefined
  })

  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: showWrap
      ? parsedAmounts[independentField]?.toExact() ?? ''
      : parsedAmounts[dependentField]?.toSignificant(6) ?? ''
  }

  const route = trade?.route
  const userHasSpecifiedInputOutput = Boolean(
    currencies[Field.INPUT] && currencies[Field.OUTPUT] && parsedAmounts[independentField]?.greaterThan(JSBI.BigInt(0))
  )
  const noRoute = !route

  // check whether the user has approved the router on the input token
  const [approval, approveCallback] = useApproveCallbackFromTrade(trade, allowedSlippage)

  // check if user has gone through approval process, used to show two step buttons, reset on token change
  const [approvalSubmitted, setApprovalSubmitted] = useState<boolean>(false)

  // mark when a user has submitted an approval, reset onTokenSelection for input field
  useEffect(() => {
    if (approval === ApprovalState.PENDING) {
      setApprovalSubmitted(true)
    }
  }, [approval, approvalSubmitted])

  const maxAmountInput: CurrencyAmount | undefined = maxAmountSpend(currencyBalances[Field.INPUT])
  const atMaxAmountInput = Boolean(maxAmountInput && parsedAmounts[Field.INPUT]?.equalTo(maxAmountInput))

  // the callback to execute the swap
  const { callback: swapCallback, error: swapCallbackError } = useSwapCallback(trade, allowedSlippage, recipient)

  const { priceImpactWithoutFee } = computeTradePriceBreakdown(trade)

  const [singleHopOnly] = useUserSingleHopOnly()

  const handleSwap = useCallback(() => {
    if (priceImpactWithoutFee && !confirmPriceImpactWithoutFee(priceImpactWithoutFee)) {
      return
    }
    if (!swapCallback) {
      return
    }
    setSwapState({ attemptingTxn: true, tradeToConfirm, showConfirm, swapErrorMessage: undefined, txHash: undefined })
    swapCallback()
      .then(hash => {
        setSwapState({ attemptingTxn: false, tradeToConfirm, showConfirm, swapErrorMessage: undefined, txHash: hash })

        ReactGA.event({
          category: 'Swap',
          action:
            recipient === null
              ? 'Swap w/o Send'
              : (recipientAddress ?? recipient) === account
              ? 'Swap w/o Send + recipient'
              : 'Swap w/ Send',
          label: [
            trade?.inputAmount?.currency?.symbol,
            trade?.outputAmount?.currency?.symbol,
            getTradeVersion(trade)
          ].join('/')
        })

        ReactGA.event({
          category: 'Routing',
          action: singleHopOnly ? 'Swap with multihop disabled' : 'Swap with multihop enabled'
        })
      })
      .catch(error => {
        console.log('error', error)
        setSwapState({
          attemptingTxn: false,
          tradeToConfirm,
          showConfirm,
          swapErrorMessage: error.message,
          txHash: undefined
        })
      })
  }, [
    priceImpactWithoutFee,
    swapCallback,
    tradeToConfirm,
    showConfirm,
    recipient,
    recipientAddress,
    account,
    trade,
    singleHopOnly
  ])

  // errors
  const [showInverted, setShowInverted] = useState<boolean>(false)

  // warnings on slippage
  const priceImpactSeverity = warningSeverity(priceImpactWithoutFee)

  // show approve flow when: no error on inputs, not approved or pending, or approved in current session
  // never show if price impact is above threshold in non expert mode
  const showApproveFlow =
    !swapInputError &&
    (approval === ApprovalState.NOT_APPROVED ||
      approval === ApprovalState.PENDING ||
      (approvalSubmitted && approval === ApprovalState.APPROVED)) &&
    !(priceImpactSeverity > 3 && !isExpertMode)

  const handleConfirmDismiss = useCallback(() => {
    setSwapState({ showConfirm: false, tradeToConfirm, attemptingTxn, swapErrorMessage, txHash })
    // if there was a tx hash, we want to clear the input
    if (txHash) {
      onUserInput(Field.INPUT, '')
    }
  }, [attemptingTxn, onUserInput, swapErrorMessage, tradeToConfirm, txHash])

  const handleAcceptChanges = useCallback(() => {
    setSwapState({ tradeToConfirm: trade, swapErrorMessage, txHash, attemptingTxn, showConfirm })
  }, [attemptingTxn, showConfirm, swapErrorMessage, trade, txHash])

  const handleInputSelect = useCallback(
    inputCurrency => {
      setApprovalSubmitted(false) // reset 2 step UI for approvals
      onCurrencySelection(Field.INPUT, inputCurrency)
    },
    [onCurrencySelection]
  )

  const handleMaxInput = useCallback(() => {
    maxAmountInput && onUserInput(Field.INPUT, maxAmountInput.toExact())
  }, [maxAmountInput, onUserInput])

  const handleOutputSelect = useCallback(outputCurrency => onCurrencySelection(Field.OUTPUT, outputCurrency), [
    onCurrencySelection
  ])

  const swapIsUnsupported = useIsTransactionUnsupported(currencies?.INPUT, currencies?.OUTPUT)

  // 获取TradingView的交易对信息
  const getTradingViewPairInfo = useMemo(() => {
    if (currencies[Field.INPUT] && currencies[Field.OUTPUT]) {
      let inputToken = currencies[Field.INPUT]
      let outputToken = currencies[Field.OUTPUT]

      // 将MARS (ETHER) 转换为WETH Token
      if (inputToken === ETHER) {
        inputToken = WETH[ChainId.MOVA] // 使用MOVA链的WETH
      }

      if (outputToken === ETHER) {
        outputToken = WETH[ChainId.MOVA]
      }

      // 现在两个都应该是Token实例，可以获取pairAddress
      if (inputToken instanceof Token && outputToken instanceof Token) {
        try {
          // 使用Pair.getAddress获取Pair地址
          const pairAddress = Pair.getAddress(inputToken, outputToken)
          return {
            pairAddress,
            inputToken: {
              symbol: inputToken.symbol,
              address: inputToken.address,
              decimals: inputToken.decimals,
              chainId: inputToken.chainId
            },
            outputToken: {
              symbol: outputToken.symbol,
              address: outputToken.address,
              decimals: outputToken.decimals,
              chainId: outputToken.chainId
            },
            symbol: `${inputToken.symbol}/${outputToken.symbol}`
          }
        } catch (error) {
          console.log('Failed to get pair address:', error)
          return {
            pairAddress: null,
            inputToken: {
              symbol: inputToken.symbol,
              address: inputToken.address,
              decimals: inputToken.decimals,
              chainId: inputToken.chainId
            },
            outputToken: {
              symbol: outputToken.symbol,
              address: outputToken.address,
              decimals: outputToken.decimals,
              chainId: outputToken.chainId
            },
            symbol: `${inputToken.symbol}/${outputToken.symbol}`
          }
        }
      } else {
        console.log('Not both Token instances, using fallback')
        // 尝试获取pairAddress，如果两个都是Token实例
        let pairAddress = null
        if (inputToken instanceof Token && outputToken instanceof Token) {
          try {
            pairAddress = Pair.getAddress(inputToken, outputToken)
          } catch (error) {
            console.warn('Failed to get pair address for Currency types:', error)
          }
        }

        const inputSymbol = inputToken?.symbol || 'UNKNOWN'
        const outputSymbol = outputToken?.symbol || 'UNKNOWN'
        return {
          pairAddress,
          inputToken:
            inputToken instanceof Token
              ? {
                  symbol: inputToken.symbol,
                  address: inputToken.address,
                  decimals: inputToken.decimals,
                  chainId: inputToken.chainId
                }
              : null,
          outputToken:
            outputToken instanceof Token
              ? {
                  symbol: outputToken.symbol,
                  address: outputToken.address,
                  decimals: outputToken.decimals,
                  chainId: outputToken.chainId
                }
              : null,
          symbol: `${inputSymbol}/${outputSymbol}`
        }
      }
    }

    // 默认返回BTCUSDT
    return {
      pairAddress: null,
      inputToken: null,
      outputToken: null
    }
  }, [currencies[Field.INPUT], currencies[Field.OUTPUT]])

  const isDarkTheme = theme.bg1 === '#212429' // 暗色主题的背景色

  const onShowChart = () => {
    if (currencies[Field.INPUT] && currencies[Field.OUTPUT]) {
      setIsShowChart(!isShowChart)
    } else {
      setMessage({ type: 'error', text: 'Please select a pair to show chart' })

      setTimeout(() => {
        setMessage({ type: '', text: '' })
      }, 3000)
      setIsShowChart(false)
    }
  }

  return (
    <>
      {message.type && <Toast type={message.type}>{message.text}</Toast>}
      <TokenWarningModal
        isOpen={importTokensNotInDefault.length > 0 && !dismissTokenWarning}
        tokens={importTokensNotInDefault}
        onConfirm={handleConfirmTokenWarning}
        onDismiss={handleDismissTokenWarning}
      />
      <MainContainer>
        {/* <Sidebar /> */}
        <ContentContainer
          align={currencies[Field.INPUT] && currencies[Field.OUTPUT] && isShowChart ? 'flex-start' : 'center'}
          isGrid={Boolean(currencies[Field.INPUT] && currencies[Field.OUTPUT] && isShowChart)}
        >
          {currencies[Field.INPUT] && currencies[Field.OUTPUT] && isShowChart && (
            <TradingViewSection>
              <TradingView
                key={`${getTradingViewPairInfo?.symbol}-${isDarkTheme ? 'dark' : 'light'}-${
                  getTradingViewPairInfo?.pairAddress
                }`}
                symbol={getTradingViewPairInfo?.symbol}
                pairAddress={getTradingViewPairInfo?.pairAddress}
                inputToken={getTradingViewPairInfo?.inputToken}
                outputToken={getTradingViewPairInfo?.outputToken}
                theme={isDarkTheme ? 'dark' : 'light'}
                interval="D"
                autosize={true}
                onShowChart={onShowChart}
                shouldInvertPrices={shouldInvertPrices}
              />
            </TradingViewSection>
          )}
          <AppBody
            style={
              currencies[Field.INPUT] && currencies[Field.OUTPUT] && isShowChart
                ? { marginLeft: '20px', padding: '20px', maxWidth: '650px' }
                : { maxWidth: '650px', padding: '40px' }
            }
          >
            <SwapHeader onShowChart={onShowChart} isShowChart={isShowChart} />
            <Wrapper id="swap-page">
              <ConfirmSwapModal
                isOpen={showConfirm}
                trade={trade}
                originalTrade={tradeToConfirm}
                onAcceptChanges={handleAcceptChanges}
                attemptingTxn={attemptingTxn}
                txHash={txHash}
                recipient={recipient}
                allowedSlippage={allowedSlippage}
                onConfirm={handleSwap}
                swapErrorMessage={swapErrorMessage}
                onDismiss={handleConfirmDismiss}
              />

              <AutoColumn gap={'sm'}>
                <CurrencyInputPanel
                  label={independentField === Field.OUTPUT && !showWrap && trade ? 'From (estimated)' : 'From'}
                  value={formattedAmounts[Field.INPUT]}
                  showMaxButton={!atMaxAmountInput}
                  currency={currencies[Field.INPUT]}
                  onUserInput={handleTypeInput}
                  onMax={handleMaxInput}
                  onCurrencySelect={handleInputSelect}
                  otherCurrency={currencies[Field.OUTPUT]}
                  id="swap-currency-input"
                />
                <AutoColumn justify="space-between">
                  {/* <AutoRow justify={isExpertMode ? 'space-between' : 'center'} style={{ padding: '0 1rem' }}> */}
                  <AutoRow justify={'center'} style={{ padding: '0 1rem', position: 'relative' }}>
                    {/* <ArrowWrapper clickable> */}
                    <SwapIconWrapper
                      onClick={() => {
                        setApprovalSubmitted(false) // reset 2 step UI for approvals
                        setShouldInvertPrices(!shouldInvertPrices)
                        onSwitchTokens()
                      }}
                    >
                      <SwapIcon src={`/images/swap_icon.svg`} alt="arrow-down" />
                    </SwapIconWrapper>
                    {/* </ArrowWrapper> */}
                    {/* {recipient === null && !showWrap && isExpertMode ? (
                      <LinkStyledButton id="add-recipient-button" onClick={() => onChangeRecipient('')}>
                        + Add a send (optional)
                      </LinkStyledButton>
                    ) : null} */}
                  </AutoRow>
                </AutoColumn>
                <CurrencyInputPanel
                  value={formattedAmounts[Field.OUTPUT]}
                  onUserInput={handleTypeOutput}
                  label={independentField === Field.INPUT && !showWrap && trade ? 'To (estimated)' : 'To'}
                  showMaxButton={false}
                  currency={currencies[Field.OUTPUT]}
                  onCurrencySelect={handleOutputSelect}
                  otherCurrency={currencies[Field.INPUT]}
                  id="swap-currency-output"
                />

                {recipient !== null && !showWrap ? (
                  <>
                    <AutoRow justify="space-between" style={{ padding: '0 1rem' }}>
                      <ArrowWrapper clickable={false}>
                        <ArrowDown size="16" color={theme.text2} />
                      </ArrowWrapper>
                      <LinkStyledButton id="remove-recipient-button" onClick={() => onChangeRecipient(null)}>
                        - Remove send
                      </LinkStyledButton>
                    </AutoRow>
                    <AddressInputPanel id="recipient" value={recipient} onChange={onChangeRecipient} />
                  </>
                ) : null}

                {showWrap ? null : (
                  <Card padding={showWrap ? '.25rem 1rem 0 1rem' : '0px'} borderRadius={'20px'}>
                    <AutoColumn gap="8px" style={{ padding: '0 16px' }}>
                      {Boolean(trade) && (
                        <RowBetween align="center">
                          <Text fontWeight={500} fontSize={14} color={theme.text2}>
                            Price
                          </Text>
                          <TradePrice
                            price={trade?.executionPrice}
                            showInverted={showInverted}
                            setShowInverted={setShowInverted}
                          />
                        </RowBetween>
                      )}
                    </AutoColumn>
                  </Card>
                )}
              </AutoColumn>
              <RowBetween align="center">
                <AdvancedSwapDetailsDropdown trade={trade} />

                <BottomGrouping>
                  {swapIsUnsupported ? (
                    <ButtonPrimary disabled={true}>
                      <TYPE.main mb="4px">Unsupported Asset</TYPE.main>
                    </ButtonPrimary>
                  ) : !account ? (
                    <Web3Status />
                  ) : showWrap ? (
                    <ButtonPrimary disabled={Boolean(wrapInputError)} onClick={onWrap}>
                      {wrapInputError ??
                        (wrapType === WrapType.WRAP ? 'Wrap' : wrapType === WrapType.UNWRAP ? 'Unwrap' : null)}
                    </ButtonPrimary>
                  ) : noRoute && userHasSpecifiedInputOutput ? (
                    <GreyCard style={{ textAlign: 'center', padding: '10px 16px', borderRadius: '50px' }}>
                      <TYPE.main mb="4px">Insufficient liquidity for this trade.</TYPE.main>
                      {singleHopOnly && <TYPE.main mb="4px">Try enabling multi-hop trades.</TYPE.main>}
                    </GreyCard>
                  ) : showApproveFlow ? (
                    <RowBetween>
                      <ButtonConfirmed
                        onClick={approveCallback}
                        disabled={approval !== ApprovalState.NOT_APPROVED || approvalSubmitted}
                        width="48%"
                        altDisabledStyle={approval === ApprovalState.PENDING} // show solid button while waiting
                        confirmed={approval === ApprovalState.APPROVED}
                      >
                        {approval === ApprovalState.PENDING ? (
                          <AutoRow gap="6px" justify="center">
                            Approving <Loader stroke="white" />
                          </AutoRow>
                        ) : approvalSubmitted && approval === ApprovalState.APPROVED ? (
                          'Approved'
                        ) : (
                          'Approve ' + currencies[Field.INPUT]?.symbol
                        )}
                      </ButtonConfirmed>
                      <ButtonError
                        onClick={() => {
                          if (isExpertMode) {
                            handleSwap()
                          } else {
                            setSwapState({
                              tradeToConfirm: trade,
                              attemptingTxn: false,
                              swapErrorMessage: undefined,
                              showConfirm: true,
                              txHash: undefined
                            })
                          }
                        }}
                        width="48%"
                        id="swap-button"
                        disabled={
                          !isValid || approval !== ApprovalState.APPROVED || (priceImpactSeverity > 3 && !isExpertMode)
                        }
                        error={isValid && priceImpactSeverity > 2}
                      >
                        <Text fontSize={16} fontWeight={500}>
                          {priceImpactSeverity > 3 && !isExpertMode
                            ? `Price Impact High`
                            : `Swap${priceImpactSeverity > 2 ? ' Anyway' : ''}`}
                        </Text>
                      </ButtonError>
                    </RowBetween>
                  ) : (
                    <ButtonError
                      onClick={() => {
                        if (isExpertMode) {
                          handleSwap()
                        } else {
                          setSwapState({
                            tradeToConfirm: trade,
                            attemptingTxn: false,
                            swapErrorMessage: undefined,
                            showConfirm: true,
                            txHash: undefined
                          })
                        }
                      }}
                      id="swap-button"
                      disabled={!isValid || (priceImpactSeverity > 3 && !isExpertMode) || !!swapCallbackError}
                      error={isValid && priceImpactSeverity > 2 && !swapCallbackError}
                    >
                      <Text fontSize={20} fontWeight={500}>
                        {swapInputError
                          ? swapInputError
                          : priceImpactSeverity > 3 && !isExpertMode
                          ? `Price Impact Too High`
                          : `Swap${priceImpactSeverity > 2 ? ' Anyway' : ''}`}
                      </Text>
                    </ButtonError>
                  )}
                  {showApproveFlow && (
                    <Column style={{ marginTop: '1rem' }}>
                      <ProgressSteps steps={[approval === ApprovalState.APPROVED]} />
                    </Column>
                  )}
                  {isExpertMode && swapErrorMessage ? <SwapCallbackError error={swapErrorMessage} /> : null}
                  {betterTradeLinkV2 && !swapIsUnsupported && toggledVersion === Version.v1 ? (
                    <BetterTradeLink version={betterTradeLinkV2} />
                  ) : toggledVersion !== DEFAULT_VERSION && defaultTrade ? (
                    <DefaultVersionLink />
                  ) : null}
                </BottomGrouping>
              </RowBetween>
            </Wrapper>
          </AppBody>
        </ContentContainer>
      </MainContainer>
    </>
  )
}
